import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import {styled} from '@mui/material/styles';
import {GoogleIcon} from '../../login/sign-in/CustomIcons';
import {Trans, useTranslation} from "react-i18next";
import {getAuth, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider} from "firebase/auth";
import functionHandler from "../../../utils/functionHandler";
import CartManager from "../../../managers/cartManager/cartManager";
import {FormHelperText} from "@mui/material";
import {useNavigate} from "react-router";


const Card = styled(MuiCard)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: '450px',
    },
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignUpContainer = styled(Stack)(({theme}) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

export default function SignUp({queryParams}) {
    const provider = new GoogleAuthProvider();
    const navigate = useNavigate();

    const auth = getAuth();
    const {t} = useTranslation();
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [error, setLoginError] = React.useState('');

    const validateInputs = () => {
        const email = document.getElementById('email');
        const password = document.getElementById('password');

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(t('auth.emailValidationErr'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage(t('auth.passwordValidationErr'));
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    const signUpWithGoogle = () => {
        signInWithPopup(auth, provider).then((result) => {
            const user = result.user;
            let payload = {user, cartId: CartManager.getCartId()};
            functionHandler.sendData('/afterRegister', payload, () => {
                navigate('/');
            });
        }).catch((error) => {
            // Handle Errors here.
            setLoginError(error.message);
        });
    };

    const handleSubmit = (event) => {
        if (emailError || passwordError) {
            event.preventDefault();
            return;
        }

        const data = new FormData(event.currentTarget);
        createUserWithEmailAndPassword(auth, data.get('email'), data.get('password'))
            .then((userCredential) => {
                // Signed up
                const user = userCredential.user;
                let payload = {user, cartId: CartManager.getCartId()};
                functionHandler.sendData('/afterRegister', payload, () => {
                    navigate('/');
                });
            }).catch((error) => {
            setLoginError(t(error.code.replace('/', '.errorsByCode.')));
        });
        event.preventDefault();
    };

    return (
        <>
            <SignUpContainer direction="column" justifyContent="space-between">
                <Card variant="outlined">
                    <Link href="/" sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Box
                            component="img"
                            sx={{
                                display: 'flex',
                                width: {xs: 150, md: 200},
                                pt: 1
                            }}
                            alt="Diwi.ro"
                            src="/diwilogo.svg"
                        />
                    </Link>
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)'}}
                    >
                        <Trans i18nKey={"auth.signUp"}/>
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{display: 'flex', flexDirection: 'column', gap: 2}}
                    >
                        <FormControl>
                            <FormLabel htmlFor="email"><Trans i18nKey={"auth.email"}/></FormLabel>
                            <TextField
                                required
                                fullWidth
                                id="email"
                                placeholder="john.snow@email.com"
                                name="email"
                                autoComplete="email"
                                variant="outlined"
                                error={emailError}
                                helperText={emailErrorMessage}
                                color={passwordError ? 'error' : 'primary'}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel htmlFor="password"><Trans i18nKey={"auth.password"}/></FormLabel>
                            <TextField
                                required
                                fullWidth
                                name="password"
                                placeholder="••••••"
                                type="password"
                                id="password"
                                autoComplete="new-password"
                                variant="outlined"
                                error={passwordError}
                                helperText={passwordErrorMessage}
                                color={passwordError ? 'error' : 'primary'}
                            />
                        </FormControl>
                        <FormHelperText error>{error}</FormHelperText>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            onClick={validateInputs}
                        >
                            <Trans i18nKey={"auth.signUp"}/>
                        </Button>
                    </Box>
                    <Divider>
                        <Typography sx={{color: 'text.secondary'}}><Trans i18nKey="auth.or"/></Typography>
                    </Divider>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={signUpWithGoogle}
                            startIcon={<GoogleIcon/>}
                        >
                            <Trans i18nKey={"auth.signUpWithGoogle"}/>
                        </Button>
                        <Typography sx={{textAlign: 'center'}}>
                            <Trans i18nKey={"auth.alreadyHaveAcc"}/>
                            <Link
                                href="/sign-in"
                                variant="body2"
                                sx={{alignSelf: 'center'}}
                            >
                                <Trans i18nKey={"auth.signIn"}/>
                            </Link>
                        </Typography>
                    </Box>
                </Card>
            </SignUpContainer>
        </>
    );
}
